import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Chip from "@/components/ui/chip";
import { ArrowRightLong, RightChevron } from "@/components/icons";
import type {
  DashboardAnnouncement,
  DashboardAnnouncementTagType,
} from "../../types/dashboard.types";
import { clientAnnouncementsApi } from "@/features/client-announcements/services/client-announcements.api";
import type { ClientAnnouncementListItem } from "@/features/client-announcements/types/client-announcements.types";
import { ArrowLeft as ArrowLeftIcon, ArrowRight as ArrowRightIcon } from "@/components/icons";
import styles from "./DashboardAnnouncementsList.module.scss";

const TAG_CHIP_TYPE: Record<
  DashboardAnnouncementTagType,
  "success" | "primary" | "warning" | "default"
> = {
  system: "primary",
  reminder: "warning",
  catalog: "success",
  education: "success",
};

const PAGE_SIZE = 10;
const AUTO_SLIDE_MS = 5000;

function formatTime(iso?: string): string {
  // Duyuruların saat bilgisi gösterilmez; yalnızca DD.MM.YY tarih formatı kullanılır.
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Tag metninden chip tipini çıkarır; bilinmeyenler "system" olarak düşer. */
function inferTagType(tag: string): DashboardAnnouncementTagType {
  const t = tag.toLowerCase();
  if (
    t.includes("eğitim") ||
    t.includes("egitim") ||
    t.includes("kurs") ||
    t.includes("education")
  ) {
    return "education";
  }
  if (t.includes("katalog") || t.includes("catalog")) return "catalog";
  if (
    t.includes("hatırlat") ||
    t.includes("hatirlat") ||
    t.includes("reminder")
  ) {
    return "reminder";
  }
  return "system";
}

function mapToListDisplay(
  item: ClientAnnouncementListItem,
): DashboardAnnouncement {
  const tag = item.tags?.[0] || "Duyuru";
  return {
    id: item.id,
    title: item.title,
    imageUrl: item.imageUrl ?? undefined,
    time: formatTime(item.publishedDate),
    description: item.descriptionSnippet,
    tag,
    tagType: inferTagType(tag),
  };
}

interface DashboardAnnouncementsListProps {
  viewAllPath?: string;
}

export const DashboardAnnouncementsList: React.FC<DashboardAnnouncementsListProps> = ({
  viewAllPath = "/dashboard/client-announcements",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<DashboardAnnouncement[]>([]);
  const [total, setTotal] = useState(0);
  const page = 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragDeltaX, setDragDeltaX] = useState(0);

  const tab = "all" as const;

  useEffect(() => {
    clientAnnouncementsApi
      .getAnnouncements({ type: tab, page, limit: PAGE_SIZE })
      .then((res) => {
        setItems((res.items || []).map(mapToListDisplay));
        setTotal(res.total ?? 0);
      })
      .catch((err) => {
        if (import.meta.env.DEV) {
          console.warn(
            "[DashboardAnnouncementsList] getAnnouncements failed",
            err,
          );
        }
        setItems([]);
        setTotal(0);
      });
  }, [page, tab]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const activeItem = items[activeIndex] ?? null;
  const hasImage = !!activeItem?.imageUrl;

  useEffect(() => {
    setActiveIndex(0);
  }, [tab, page, items.length]);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, AUTO_SLIDE_MS);
    return () => clearInterval(timer);
  }, [items]);

  const goPrev = () => {
    if (!items.length) return;
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const goNext = () => {
    if (!items.length) return;
    setActiveIndex((prev) => (prev + 1) % items.length);
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>
          {t("dashboard.announcements.title")}
        </h3>
        <button
          type="button"
          className={styles.sectionLink}
          onClick={() =>
            navigate(viewAllPath, {
              state: { activeTab: 0 },
            })
          }
          aria-label={t("dashboard.announcements.viewAll")}
        >
          <RightChevron width={20} height={20} />
        </button>
      </div>
      <ul className={styles.list}>
        {activeItem ? (
          <li
            key={activeItem.id}
            className={`${styles.item} ${hasImage ? styles.itemWithImage : styles.itemNoImage}`}
            onMouseDown={(e) => {
              setDragStartX(e.clientX);
              setDragDeltaX(0);
            }}
            onMouseMove={(e) => {
              if (dragStartX == null) return;
              setDragDeltaX(e.clientX - dragStartX);
            }}
            onMouseUp={() => {
              if (dragStartX == null) return;
              if (dragDeltaX > 60) goPrev();
              else if (dragDeltaX < -60) goNext();
              setDragStartX(null);
              setDragDeltaX(0);
            }}
            onMouseLeave={() => {
              setDragStartX(null);
              setDragDeltaX(0);
            }}
          >
            <div
              className={styles.itemBackdrop}
              style={
                activeItem.imageUrl
                  ? { backgroundImage: `url(${activeItem.imageUrl})` }
                  : undefined
              }
            />
            <div className={styles.itemImageWrap}>
              {activeItem.imageUrl ? (
                <img src={activeItem.imageUrl} alt="" className={styles.itemImage} />
              ) : (
                <div className={styles.itemImagePlaceholder} />
              )}
            </div>
            <button
              type="button"
              className={`${styles.navOverlayBtn} ${styles.navOverlayPrev}`}
              onClick={goPrev}
              aria-label="Önceki duyuru"
            >
              <ArrowLeftIcon height={18} width={18} />
            </button>
            <button
              type="button"
              className={`${styles.navOverlayBtn} ${styles.navOverlayNext}`}
              onClick={goNext}
              aria-label="Sonraki duyuru"
            >
              <ArrowRightIcon height={18} width={18} />
            </button>
            <div className={styles.itemBody}>
              <div className={styles.itemTopRow}>
                <h4 className={styles.itemTitle}>{activeItem.title}</h4>
                {activeItem.time && (
                  <span className={styles.itemTime}>{activeItem.time}</span>
                )}
              </div>
              <div className={styles.itemBottomRow}>
                {activeItem.description && (
                  <p className={styles.itemDesc}>{activeItem.description}</p>
                )}
                <div className={styles.metaRow}>
                  <span className={styles.tagWrap}>
                    <Chip
                      type={TAG_CHIP_TYPE[activeItem.tagType]}
                      variant="soft"
                      size="small"
                    >
                      {activeItem.tag}
                    </Chip>
                  </span>
                  <div className={styles.ctaRow}>
                    <button
                      type="button"
                      className={styles.detailLink}
                      onClick={() =>
                        navigate(viewAllPath, {
                          state: { activeTab: 0 },
                        })
                      }
                    >
                      {t("dashboard.announcements.detail")}
                      <ArrowRightLong
                        width={14}
                        height={14}
                        color="#E7212B"
                        className={styles.detailArrow}
                      />
                    </button>
                    <button
                      type="button"
                      className={styles.detailSecondary}
                      onClick={() =>
                        navigate(viewAllPath, {
                          state: { activeTab: 0 },
                        })
                      }
                    >
                      {t("dashboard.announcements.viewAll")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </li>
        ) : null}
      </ul>
      {(items.length > 1 || totalPages > 1) && (
        <div className={styles.pagination}>
          <div className={styles.dotsWrap}>
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                className={`${styles.dot} ${i === activeIndex ? styles.dotActive : ""}`}
                onClick={() => setActiveIndex(i)}
                aria-label={`${i + 1}. duyuru`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardAnnouncementsList;
